import React, { ReactNode } from "react"
import { Form, FormRenderProps, FormSpy } from "react-final-form"
import cn from "classnames";
import ContinueButton from "../controls/ContinueButton"
import UserTermsLabel from "../texts/UserTermsLabel"
import InfoBox from "../boxes/InfoBox"
import ResetPasswordLink from "./ResetPasswordLink"
import SubmitError from "../form-fields/SubmitError"
import SwitchField from "../form-fields/SwitchField"
import TextField from "../form-fields/TextField"
import userApi, { EmailUser } from "../../api/user"
import { SignUpFormProps } from "./types"
import { UserRole } from "../../types/user"
import { getSavedEmailValues, navigateToNextStep, saveEmailValues } from "./utils"

const getInitialValues = (role: UserRole = "borrower"): EmailUser => {
  const savedValues = getSavedEmailValues();
  if (savedValues && savedValues.role === role) return savedValues;
  return {
    email: "",
    password: "",
    rememberMe: false,
    role,
    termsOfUse: false,
  }
}

const EmailPasswordForm = ({ isUserExists, signIn, userRole, withAutoFocus }: SignUpFormProps): JSX.Element => {

  const submitForm = async (values: EmailUser): Promise<FormErrors | void> => {
    return (signIn ? userApi.signIn : userApi.createUser)(values)
      .then(navigateToNextStep)
      .catch((errors: FormErrors) => errors)
  }

  return (
    <Form
      initialValues={getInitialValues(userRole)}
      onSubmit={submitForm}
      render={(renderProps: FormRenderProps<EmailUser>): ReactNode => {
        const { handleSubmit, submitError, submitting } = renderProps
        const textFieldClass = cn("Form-field", { "Form-field--280": !isUserExists });
        return (
          <div className="mz-new-user-fields u-max-w-580">
            <FormSpy onChange={(spyProps) => saveEmailValues(spyProps.values as EmailUser)}/>
            <form id="form_email_auth" onSubmit={handleSubmit}>
              <TextField
                autoFocus={withAutoFocus}
                className={textFieldClass}
                i18key="attributes.email"
                id="field_email"
                name="email"
                type="email"
              />
              <TextField
                className={textFieldClass}
                i18key="attributes.password"
                id="field_password"
                name="password"
                type="password"
              />
              <InfoBox i18nKey="registration.tips.password"/>
              <SubmitError className="u-mt-40" errors={submitError}/>
              {signIn && (
                <SwitchField
                  className="u-mt-30"
                  i18nKey="activerecord.attributes.user.remember_me"
                  id="swtch_remember_me"
                  name="rememberMe"
                />
              )}
              {!signIn && (
                <SwitchField className="u-mt-30" id="swtch_terms_of_use" name="termsOfUse">
                  <UserTermsLabel role={userRole}/>
                </SwitchField>
              )}
              {signIn && <div className="u-mt-lg"><ResetPasswordLink/></div>}
              <ContinueButton
                className={`Form-submit ${signIn ? "u-min-w-140" : "u-min-w-208"}`}
                disabled={submitting}
                i18nKey={`controls.buttons.${signIn ? "sign_in" : "continue"}`}
                secondary={!signIn && userRole === "investor"}
              />
            </form>
          </div>
        )
      }}
    />
  )
}

export default EmailPasswordForm
